// Cart slice. The cart itself lives server-side (the gateway owns pricing,
// stock and line totals); this slice only persists the cart *id* in
// localStorage and mirrors the hydrated `Cart` the server returns. Every
// mutation round-trips through `api.cart.*` and stores whatever comes back —
// the client never computes totals.
//
// Robustness: the gateway keeps carts in memory, so a server restart (or an
// expired id) makes the stored id dangling. `refresh()` silently drops a
// 404'd id, and `add()` transparently recreates the cart once if the id went
// stale mid-session — so a returning buyer never hits a hard error.
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { type AddCartItemRequest, api, type Cart } from "@/api";
import { trackCartAdd } from "@/lib/discovery";
import { ecCartDiff, METRIKA_GOALS, reachGoal } from "@/lib/metrika-ecommerce";

import { safeLocalStorage } from "./safe-storage";

const isMissingCart = (e: unknown) =>
	e instanceof Error && /cart not found/i.test(e.message);

type CartState = {
	cartId: string | null;
	cart: Cart | null;
	loading: boolean;
	/** Items count for the header badge — 0 until the cart is hydrated. */
	count: () => number;
	/** Create the server cart on first need; returns the live id. */
	ensureId: () => Promise<string>;
	/** Re-pull the cart from the server; drops the id if the server lost it. */
	refresh: () => Promise<void>;
	add: (req: AddCartItemRequest) => Promise<void>;
	acceptOffer: (offerId: string) => Promise<void>;
	setQty: (itemId: string, qty: number) => Promise<void>;
	remove: (itemId: string) => Promise<void>;
	removeMany: (itemIds: string[]) => Promise<void>;
	/** «Скидка OSMIX»: применить/снять промокод корзины. */
	applyPromo: (code: string) => Promise<void>;
	clearPromo: () => Promise<void>;
	/** Forget the local cart after a successful order. */
	clear: () => void;
	/** Переключить устройство на корзину аккаунта (ответ слияния гость → аккаунт). */
	adopt: (cart: Cart | null) => void;
};

export const useCart = create<CartState>()(
	persist(
		(set, get) => ({
			cartId: null,
			cart: null,
			loading: false,
			count: () => get().cart?.itemCount ?? 0,
			ensureId: async () => {
				const existing = get().cartId;
				if (existing) return existing;
				const cart = await api.cart.create();
				set({ cartId: cart.id, cart });
				return cart.id;
			},
			refresh: async () => {
				const id = get().cartId;
				if (!id) return;
				set({ loading: true });
				try {
					const cart = await api.cart.get(id);
					set({ cart, loading: false });
				} catch (e) {
					if (isMissingCart(e)) set({ cartId: null, cart: null, loading: false });
					else set({ loading: false });
				}
			},
			add: async (req) => {
				const id = await get().ensureId();
				// Метрика: «добавление в корзину» - разница корзины до и после.
				const before = get().cart;
				try {
					set({ cart: await api.cart.addItem(id, req) });
				} catch (e) {
					if (!isMissingCart(e)) throw e;
					// id went stale (server restart) — recreate once and retry.
					const fresh = await api.cart.create();
					set({ cartId: fresh.id, cart: await api.cart.addItem(fresh.id, req) });
				}
				// Третья ступень воронки продавца. Считаем здесь, а не на кнопках:
				// «В корзину» живёт на карточке товара, в каталоге, в быстром
				// просмотре и в подборке — все они приходят сюда, и только сюда.
				// Событие уходит после успешного ответа: неудавшееся добавление
				// корзиной не было.
				trackCartAdd(req.productId);
				ecCartDiff(before, get().cart);
			},
			acceptOffer: async (offerId) => {
				const id = await get().ensureId();
				try {
					set({ cart: await api.buyer.acceptOffer(offerId, id) });
				} catch (e) {
					if (!isMissingCart(e)) throw e;
					const fresh = await api.cart.create();
					set({ cartId: fresh.id, cart: await api.buyer.acceptOffer(offerId, fresh.id) });
				}
			},
			// Смена количества и удаление - события Метрики `add` / `remove`
			// по разнице корзины (lib/metrika-ecommerce).
			setQty: async (itemId, qty) => {
				const id = get().cartId;
				if (!id) return;
				const before = get().cart;
				set({ cart: await api.cart.updateItem(id, itemId, qty) });
				ecCartDiff(before, get().cart);
			},
			remove: async (itemId) => {
				const id = get().cartId;
				if (!id) return;
				const before = get().cart;
				set({ cart: await api.cart.removeItem(id, itemId) });
				ecCartDiff(before, get().cart);
			},
			removeMany: async (itemIds) => {
				if (itemIds.length === 0) return;
				const id = await get().ensureId();
				const before = get().cart;
				set({ cart: await api.cart.removeItems(id, itemIds) });
				ecCartDiff(before, get().cart);
			},
			applyPromo: async (code) => {
				const id = await get().ensureId();
				set({ cart: await api.cart.applyPromo(id, code) });
				// Цель «Применение промокода» - только после принятого шлюзом кода.
				reachGoal(METRIKA_GOALS.promoApplied);
			},
			clearPromo: async () => {
				const id = get().cartId;
				if (!id) return;
				set({ cart: await api.cart.clearPromo(id) });
			},
			clear: () => set({ cartId: null, cart: null }),
			adopt: (cart) => set({ cartId: cart?.id ?? null, cart }),
		}),
		{
			name: "osmix-cart",
			// Тот же безопасный адаптер, что у избранного: запрет записи в
			// localStorage не должен ронять «В корзину» и выход из аккаунта.
			storage: createJSONStorage(() => safeLocalStorage),
			partialize: (s) => ({ cartId: s.cartId }),
		},
	),
);
